import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Camera, Check, RotateCcw, Trash2, Image as ImageIcon } from 'lucide-react';

interface CameraCaptureProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotosCapture: (photos: string[]) => void;
  title?: string;
}

export default function CameraCapture({
  isOpen,
  onClose,
  onPhotosCapture,
  title = "Camera"
}: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');

  // Initialize camera when modal opens
  useEffect(() => {
    if (isOpen) {
      startCamera();
    } else {
      stopCamera();
      setCapturedPhotos([]);
      setCameraError(null);
    }

    return () => {
      stopCamera();
    };
  }, [isOpen, facingMode]);

  const startCamera = async () => {
    try {
      setCameraError(null);
      
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });

      setStream(mediaStream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.play();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setCameraError('Unable to access camera. Please check permissions.');
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturing(true);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw the current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert to blob and create URL
    canvas.toBlob((blob) => {
      if (blob) {
        const photoUrl = URL.createObjectURL(blob);
        setCapturedPhotos(prev => [...prev, photoUrl]);
      }
      setIsCapturing(false);
    }, 'image/jpeg', 0.8);
  };

  const deletePhoto = (index: number) => {
    setCapturedPhotos(prev => {
      const newPhotos = prev.filter((_, i) => i !== index);
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index]);
      return newPhotos;
    });
  };

  const switchCamera = () => {
    setFacingMode(prev => prev === 'user' ? 'environment' : 'user');
  };

  const handleDone = () => {
    onPhotosCapture(capturedPhotos);
    onClose();
  };

  const handleClose = () => {
    // Clean up photo URLs
    capturedPhotos.forEach(url => URL.revokeObjectURL(url));
    setCapturedPhotos([]);
    onClose();
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed bg-black"
      style={{
        zIndex: 100200,
        top: 'env(safe-area-inset-top, 0px)',
        bottom: 'env(safe-area-inset-bottom, 0px)',
        left: 'env(safe-area-inset-left, 0px)',
        right: 'env(safe-area-inset-right, 0px)',
        width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
        height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
      }}
    >
      {/* Header - sits below notch */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-black/80 to-transparent p-4">
        <div className="flex items-center justify-between text-white">
          <button
            onClick={handleClose}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
          
          <h3 className="text-lg font-semibold">{title}</h3>
          
          <button
            onClick={switchCamera}
            className="p-2 rounded-full bg-black/50 hover:bg-black/70 transition-colors"
          >
            <RotateCcw className="h-6 w-6" />
          </button>
        </div>
      </div>

      {/* Camera View */}
      <div className="relative w-full h-full flex flex-col">
        {cameraError ? (
          <div className="flex-1 flex items-center justify-center bg-gray-900 text-white text-center p-4">
            <div>
              <Camera className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg mb-2">Camera Error</p>
              <p className="text-sm text-gray-300">{cameraError}</p>
              <button
                onClick={startCamera}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Video Stream */}
            <video
              ref={videoRef}
              className="flex-1 w-full h-full object-cover"
              autoPlay
              playsInline
              muted
            />
            
            {/* Hidden canvas for photo capture */}
            <canvas ref={canvasRef} className="hidden" />
          </>
        )}

        {/* Photo Thumbnails */}
        {capturedPhotos.length > 0 && (
          <div className="absolute top-20 right-4 z-10">
            <div className="bg-black/50 rounded-lg p-2 max-w-[120px]">
              <p className="text-white text-xs mb-2 text-center">
                {capturedPhotos.length} photo{capturedPhotos.length !== 1 ? 's' : ''}
              </p>
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {capturedPhotos.map((photo, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={photo}
                      alt={`Photo ${index + 1}`}
                      className="w-20 h-20 object-cover rounded border-2 border-white/20"
                    />
                    <button
                      onClick={() => deletePhoto(index)}
                      className="absolute -top-1 -right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Controls */}
        <div className="absolute bottom-0 left-0 right-0 z-10 bg-gradient-to-t from-black/80 to-transparent p-6">
          <div className="flex items-center justify-center space-x-8">
            {/* Photo library (no capture attr — opens Photos/Files, not camera) */}
            <button
              type="button"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.multiple = true;
                input.onchange = (e) => {
                  const files = (e.target as HTMLInputElement).files;
                  if (files) {
                    Array.from(files).forEach(file => {
                      const url = URL.createObjectURL(file);
                      setCapturedPhotos(prev => [...prev, url]);
                    });
                  }
                };
                input.click();
              }}
              className="p-3 rounded-full bg-gray-600/80 text-white hover:bg-gray-500/80 transition-colors"
              aria-label="Upload from library"
            >
              <ImageIcon className="h-6 w-6" />
            </button>

            {/* Capture Button */}
            <button
              onClick={capturePhoto}
              disabled={isCapturing || !!cameraError}
              className={`p-4 rounded-full border-4 border-white transition-all ${
                isCapturing 
                  ? 'bg-gray-400 scale-95' 
                  : 'bg-white hover:bg-gray-100 hover:scale-105 active:scale-95'
              }`}
            >
              <div className="w-12 h-12 rounded-full bg-transparent" />
            </button>

            {/* Done Button */}
            <button
              onClick={handleDone}
              disabled={capturedPhotos.length === 0}
              className={`p-3 rounded-full transition-colors ${
                capturedPhotos.length > 0
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-gray-600/50 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Check className="h-6 w-6" />
            </button>
          </div>
          
          {capturedPhotos.length > 0 && (
            <p className="text-center text-white text-sm mt-2">
              Tap ✓ to add {capturedPhotos.length} photo{capturedPhotos.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
